import {
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";

import styles from "./ReportsSlider.module.css";

import CostBreakdownReport
    from "../CostBreakdownReport/CostBreakdownReport";

import CostBreakdownSummary
    from "../CostBreakdownSummary/CostBreakdownSummary";

import MaintenanceIntervalReport
    from "../MaintenanceIntervalReport/MaintenanceIntervalReport";

import CostDashboardReport
    from "../CostDashboardReport/CostDashboardReport";


const ReportsSlider = ({
    data,
    loading = false,
}) => {

    const [activeIndex, setActiveIndex] =
        useState(0);

    const [viewportHeight, setViewportHeight] =
        useState(null);

    const viewportRef = useRef(null);

    const slideRefs = useRef([]);

    const touchStartX = useRef(null);


    const reports = data
    ? [
        {
            key: "costBreakdown",
            content: (
                <CostBreakdownReport
                    data={data.costBreakdown}
                />
            ),
        },

        {
            key: "costBreakdownSummary",
            content: (
                <CostBreakdownSummary
                    data={data.costBreakdown}
                />
            ),
        },

        {
            key: "costDashboard",
            content: (
                <CostDashboardReport
                    data={data.costDashboard}
                />
            ),
        },

        {
            key: "maintenanceInterval",
            content: (
                <MaintenanceIntervalReport
                    data={data.maintenanceInterval}
                />
            ),
        },
    ]
    : [];


    const count = reports.length;


    const goNext = () => {
        if (!count) return;

        setActiveIndex(
            (current) =>
                (current + 1) % count
        );
    };


    const goPrevious = () => {
        if (!count) return;

        setActiveIndex(
            (current) =>
                (current - 1 + count) % count
        );
    };


    /*
     * اندازه اسلاید فعال را محاسبه می‌کنیم
     * تا viewport دقیقاً به اندازه همان اسلاید باشد.
     */
    const updateViewportHeight = () => {

        const activeSlide =
            slideRefs.current[activeIndex];

        if (!activeSlide) {
            return;
        }

        const height =
            activeSlide.scrollHeight;

        if (height > 0) {
            setViewportHeight(height);
        }
    };


    useLayoutEffect(() => {

        updateViewportHeight();

        const activeSlide =
            slideRefs.current[activeIndex];

        if (!activeSlide) {
            return;
        }

        /*
         * اگر اندازه محتوا به هر دلیلی تغییر کرد
         * دوباره ارتفاع viewport را تنظیم می‌کنیم.
         */
        const resizeObserver =
            new ResizeObserver(() => {
                updateViewportHeight();
            });

        resizeObserver.observe(
            activeSlide
        );

        return () => {
            resizeObserver.disconnect();
        };

    }, [
        activeIndex,
        data,
        count,
    ]);


    /*
     * برای اطمینان از اندازه‌گیری درست
     * بعد از تغییرات DOM دوباره اندازه می‌گیریم.
     */
    useEffect(() => {

        const handleResize = () => {
            updateViewportHeight();
        };

        window.addEventListener(
            "resize",
            handleResize
        );

        return () => {
            window.removeEventListener(
                "resize",
                handleResize
            );
        };

    }, [activeIndex]);


    /*
     * اگر تعداد اسلایدها تغییر کرد،
     * activeIndex از محدوده خارج نشود.
     */
    useEffect(() => {

        if (activeIndex >= count) {
            setActiveIndex(
                Math.max(0, count - 1)
            );
        }

    }, [
        count,
        activeIndex,
    ]);


    /*
     * کنترل با کلیدهای کیبورد
     */
    useEffect(() => {

        const handleKeyDown = (event) => {

            if (event.key === "ArrowLeft") {
                goNext();
            }

            if (event.key === "ArrowRight") {
                goPrevious();
            }
        };

        window.addEventListener(
            "keydown",
            handleKeyDown
        );

        return () => {
            window.removeEventListener(
                "keydown",
                handleKeyDown
            );
        };

    }, [count]);


    /*
     * شروع Swipe
     */
    const handleTouchStart = (event) => {

        touchStartX.current =
            event.touches[0].clientX;
    };


    /*
     * پایان Swipe
     */
    const handleTouchEnd = (event) => {

        if (
            touchStartX.current === null
        ) {
            return;
        }

        const endX =
            event.changedTouches[0].clientX;

        const distance =
            endX - touchStartX.current;


        if (Math.abs(distance) < 50) {

            touchStartX.current = null;

            return;
        }


        if (distance < 0) {
            goNext();
        } else {
            goPrevious();
        }


        touchStartX.current = null;
    };


    /*
     * Loading
     */
    if (loading) {

        return (
            <section
                className={styles.wrapper}
            >
                <div
                    className={styles.loading}
                >
                    در حال دریافت گزارش...
                </div>
            </section>
        );
    }


    /*
     * Empty
     */
    if (!count) {

        return (
            <section
                className={styles.wrapper}
            >
                <div
                    className={styles.empty}
                >
                    گزارشی برای نمایش وجود ندارد.
                </div>
            </section>
        );
    }


    return (
        <section
            className={styles.wrapper}
        >

            <div
                ref={viewportRef}
                className={styles.viewport}
                style={{
                    height:
                        viewportHeight
                            ? `${viewportHeight}px`
                            : "auto",
                }}
                onTouchStart={
                    handleTouchStart
                }
                onTouchEnd={
                    handleTouchEnd
                }
            >

                <div
                    className={styles.track}
                    style={{
                        transform:
                            `translate3d(calc(-${activeIndex} * 100%), 0, 0)`,
                    }}
                >

                    {reports.map(
                        (report, index) => (

                            <div
                                key={report.key}
                                ref={(element) => {
                                    slideRefs.current[
                                        index
                                    ] = element;
                                }}
                                className={
                                    styles.slide
                                }
                            >
                                {report.content}
                            </div>
                        )
                    )}

                </div>

            </div>


            <div
                className={styles.navigation}
            >

                <button
                    type="button"
                    onClick={goPrevious}
                    aria-label="گزارش قبلی"
                >
                    ‹
                </button>


                <div
                    className={styles.indicators}
                >

                    {reports.map(
                        (report, index) => (

                            <button
                                key={report.key}
                                type="button"
                                aria-label={
                                    `گزارش ${index + 1}`
                                }
                                className={
                                    index === activeIndex
                                        ? styles.activeIndicator
                                        : styles.indicator
                                }
                                onClick={() =>
                                    setActiveIndex(
                                        index
                                    )
                                }
                            />
                        )
                    )}

                </div>


                <button
                    type="button"
                    onClick={goNext}
                    aria-label="گزارش بعدی"
                >
                    ›
                </button>

            </div>

        </section>
    );
};


export default ReportsSlider;